import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface ServiceCardProps {
  title: string;
  description: string;
  href: string;
  index: number;
  imageSrc?: string;
}

const services = [
  {
    title: 'Tonte de pelouse',
    description: 'Réalisée avec des équipements de pointe et une équipe expérimentée, garantissant un gazon impeccable et une expérience sans tracas.',
    href: '/services#tonte-de-pelouse',
    imageSrc: '/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png'
  },
  {
    title: 'Ouverture de terrain',
    description: 'Nettoyage printanier pour une cour prête à profiter de l\'été.',
    href: '/services#ouverture-de-terrain',
    imageSrc: '/lovable-uploads/b0b1beb8-b76c-40e5-b40a-ff68b25f5cb8.png'
  },
  {
    title: 'Ménage d\'automne',
    description: 'Préparation du terrain pour l\'hiver, incluant le nettoyage des feuilles et la protection des plates-bandes.',
    href: '/services#menage-automne',
    imageSrc: '/lovable-uploads/4d4550a3-8c7a-4d2f-a660-b9d322ef85ea.png'
  },
  {
    title: 'Nettoyage de stationnement',
    description: 'Élimination des roches, gravier, déchets et feuilles accumulés durant l\'hiver, assurant un stationnement propre pour la saison estivale.',
    href: '/services#nettoyage-stationnement',
    imageSrc: '/lovable-uploads/50bd2caa-82d3-46f1-b7f0-d757c8367a17.png'
  },
  {
    title: 'Déneigement de toitures',
    description: 'Service professionnel adapté aux résidences et entreprises, avec une approche préventive pour éviter l\'accumulation excessive de neige.',
    href: '/services#deneigement-toitures',
    imageSrc: '/lovable-uploads/62bd1a0b-0d2f-4518-ad22-0068ee7a9db4.png'
  },
  {
    title: 'Terrassement / paysagement',
    description: 'Transformez votre extérieur avec Entretiens McBain!\n\nQue ce soit pour du terrassement, de l’aménagement paysager, la création de plates-bandes, la restauration de pavé ou tout autre projet extérieur, notre équipe met son savoir-faire à votre service pour créer un espace soigné, durable et à la hauteur de vos attentes.',
    href: '/services#deneigement-manuel',
    imageSrc: '/__l5e/assets-v1/d7f290aa-4180-4151-a9b8-8cb8764079ad/terrassement-pave.jpg'
  }
];

const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, href, index, imageSrc }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('revealed');
            }, index * 100);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [index]);
  
  return (
    <div 
      ref={cardRef} 
      className="reveal-on-scroll bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
    >
      {imageSrc && (
        <div className="h-48 overflow-hidden">
          <img 
            src={imageSrc} 
            alt={title} 
            className="w-full h-full object-cover service-image"
          />
        </div>
      )}
      <div className="p-6 md:p-8 flex flex-col flex-grow">
        <h3 className="text-xl md:text-2xl font-display font-medium mb-3 group-hover:text-brand-green transition-colors">
          {title}
        </h3>
        <p className="text-gray-600 mb-5">
          {description}
        </p>
        <div className="mt-auto">
          <Link 
            to={href} 
            className="inline-flex items-center px-4 py-2 border-2 border-[#1e312b] text-[#1e312b] rounded-md hover:bg-[#1e312b] hover:text-white transition-colors font-medium"
          >
            En savoir plus
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};
  
const Services: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === titleRef.current) {
              entry.target.classList.add('revealed');
            } else if (entry.target === descRef.current) {
              setTimeout(() => {
                entry.target.classList.add('revealed');
              }, 200);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    [titleRef, descRef].forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      [titleRef, descRef].forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);
  
  return (
    <section ref={sectionRef} className="section bg-brand-stone-light py-20">
      <div className="container-wide">
        <div className="text-center mb-12 md:mb-16">
          <h2 ref={titleRef} className="section-title reveal-on-scroll">Nos Services</h2>
          <p ref={descRef} className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto reveal-on-scroll">
            Nous offrons une gamme complète de services d'entretien paysager conçus pour transformer vos espaces extérieurs en environnements magnifiques et fonctionnels.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              title={service.title}
              description={service.description}
              href={service.href}
              index={index}
              imageSrc={service.imageSrc}
            />
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <Button href="/services" size="lg">
            Voir tous nos services
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
